/**
 * NAV-5.1 / NAV-5.3 — one count source for sidebar badges.
 *
 * ⚠️ A BADGE AND THE PAGE IT POINTS AT MUST NOT DISAGREE. The Overview reads the
 * same functions (`countHeldLines`, `splitAgreements(...).outstanding`) and, after
 * any write in one of its panels, calls `notifyCommissionAreaCountsChanged()` so
 * these re-read. Do not compute a second number in the sidebar.
 *
 * ⚠️ `null` MEANS NOT ASKED OR FAILED, NEVER ZERO. A failed read rendered as 0
 * is the empty-list defect wearing a badge: the caller could not ask, and the
 * product reports that nothing needs attention.
 *
 * Held-line work has its own Money held badge, so Overview does not also
 * display that count. Setup counts `not_configured` readiness checks only —
 * folding `not_needed` in would make a correctly configured flat-rate account
 * report a permanent problem it can never clear (`readinessGapCount`).
 */

export type CommissionAreaCounts = {
  heldLines: number | null;
  unsignedAgreements: number | null;
  setupGaps: number | null;
};

export const EMPTY_COMMISSION_AREA_COUNTS: CommissionAreaCounts = {
  heldLines: null,
  unsignedAgreements: null,
  setupGaps: null,
};

/** Hide a badge at 0 and while unknown. Never render "0" as a claim. */
export function badgeValue(n: number | null): number | null {
  if (n === null || n <= 0) return null;
  return n;
}

export function overviewBadgeCount(counts: CommissionAreaCounts): number | null {
  return badgeValue(counts.unsignedAgreements);
}

export function moneyHeldBadgeCount(counts: CommissionAreaCounts): number | null {
  return badgeValue(counts.heldLines);
}

export function setupBadgeCount(counts: CommissionAreaCounts): number | null {
  return badgeValue(counts.setupGaps);
}

/**
 * What the collapsed Commission group shows: the sum of every destination
 * badge, so hiding the rows does not hide that work exists. Unknown sources
 * contribute nothing rather than zero.
 */
export function rolledBadgeCount(counts: CommissionAreaCounts): number | null {
  const parts = [
    overviewBadgeCount(counts),
    moneyHeldBadgeCount(counts),
    setupBadgeCount(counts),
  ];
  const known = parts.filter((n): n is number => n !== null);
  if (known.length === 0) return null;
  return badgeValue(known.reduce((sum, n) => sum + n, 0));
}

export function badgeForDestination(
  id: 'overview' | 'money-held' | 'setup' | string,
  counts: CommissionAreaCounts,
): number | null {
  switch (id) {
    case 'overview':
      return overviewBadgeCount(counts);
    case 'money-held':
      return moneyHeldBadgeCount(counts);
    case 'setup':
      return setupBadgeCount(counts);
    default:
      return null;
  }
}
